/**
 * A per-key async mutex.
 *
 * Implemented as a promise-chain lock: each acquirer chains its work onto the
 * tail of the previous acquirer's promise, so the supplied `fn` always runs
 * under exclusive access for that key and the lock is released in `finally`.
 * This guarantees real mutual exclusion — queued callers actually execute their
 * `fn` (the previous broken implementation resolved waiters with `undefined`
 * without ever running their work, allowing two callers to run concurrently).
 */
export class AsyncLock {
  // Maps a key to the tail of its pending-operation chain.
  private tails: Map<string, Promise<void>> = new Map();
  // Tracks how many callers are currently holding or waiting on each key.
  private pending: Map<string, number> = new Map();

  async acquire<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const previous = this.tails.get(key) ?? Promise.resolve();
    this.pending.set(key, (this.pending.get(key) ?? 0) + 1);

    // Run `fn` only after the previous holder for this key has settled.
    const run = previous.then(() => fn());

    // The chain tail must never reject, or every subsequent waiter would reject
    // too. Swallow the result/error here; the real result is returned via `run`.
    this.tails.set(
      key,
      run.then(
        () => undefined,
        () => undefined
      )
    );

    try {
      return await run;
    } finally {
      const remaining = (this.pending.get(key) ?? 1) - 1;
      if (remaining <= 0) {
        // No one else is queued — drop the chain so the maps don't grow forever.
        this.pending.delete(key);
        this.tails.delete(key);
      } else {
        this.pending.set(key, remaining);
      }
    }
  }

  isLocked(key: string): boolean {
    return (this.pending.get(key) ?? 0) > 0;
  }

  getQueueLength(key: string): number {
    // Number of callers waiting behind the one currently holding the lock.
    return Math.max(0, (this.pending.get(key) ?? 0) - 1);
  }
}
